import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { scroller } from 'react-scroll';
import queryString from 'query-string';
import { DocumentHeader } from '../../components';
import { FileUpload } from '../../container';
import Layout from '../../layout';
import { DocumentContext } from '../../context/DocumentProvider';
import { tryParseJson, normalizeString } from 'lib/utils/helper';
import { getTextToHighlight } from './highlighter';
import PdfFileViewer from '../PDFViewer/PdfFileViewer';
import BodySection from './BodySection';
import GlobalStyles from '../../styles/Global.style';

export const CDAMainViewer = ({
  ehrRecord,
  requestPatientRecord,
  isNewRecordAvailable,
  exportCDAAsPDF,
  exportCDAAsPDFLoading,
  setExportCDAAsPDFLoading
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const cdaUrl = queryString.parse(searchParams.toString())?.cdaUrl;
  const [rowsToExport, setRowsToExport] = useState([]);
  const [fileName, setFileName] = useState('');
  const [resetRows, setResetRows] = useState(false);
  const { currentDocument, rawDocument } = useContext(DocumentContext);
  const params = queryString.parse(searchParams.toString() || '');

  const getRowsToHighlight = (searchDataGroupedByCategory, searchText) => {
    return getTextToHighlight(searchDataGroupedByCategory, searchText);
  };

  const mainHighlighter = () => {
    let filteredData = {};
    let found = false;
    let searchDataToUse = currentDocument.searchData;

    if (params.section) {
      const normalizedSection = params.section.toLowerCase().trim();
      searchDataToUse = currentDocument.searchData.filter((suggestion) => suggestion.groupTitle?.toLowerCase().trim() === normalizedSection);
      
      if (!searchDataToUse.length) {
        searchDataToUse = currentDocument.searchData;
      }
    }

    searchDataToUse.forEach((suggestion) => {
      filteredData[suggestion.groupTitle] = [];

      const sortData = suggestion.data
        .filter((i) => {
          if (i && i.text) {
            const normalizedData = normalizeString(i.text);
            const normalizedInput = normalizeString(params.q);

            const startCondition = normalizedData.startsWith(normalizedInput);
            const includeCondition = normalizedData.includes(normalizedInput);

            if (startCondition) {
              return startCondition;
            } else if (!startCondition && includeCondition) {
              return includeCondition;
            } else {
              return null;
            }
          }

          return null;
        })
        .slice(0, 1);

      filteredData[suggestion.groupTitle].push(...sortData);
    });

    found = Object.keys(filteredData).some((category) => {
      if (filteredData[category] && filteredData[category][0]) {
        const type = filteredData[category][0].type;
        const rowId = filteredData[category][0].rowId;

        const section = filteredData[category][0].section;
        const text = filteredData[category][0].text;

        const currentParams = {};

        for (let entry of searchParams.entries()) {
          currentParams[entry[0]] = entry[1];
        }

        const newParams = {
          ...currentParams,
          q: ''
        };

        if (type === 'table' && rowId) {
          newParams.rowId = rowId;
        }

        if (type === 'text' && rowId && text) {
          newParams.rowId = rowId;
          newParams.search = text;
        }

        console.log('newParams: ', newParams);

        setSearchParams(newParams);

        return true;
      } else {
        return false;
      }
    });

    return found;
  };

  useEffect(() => {
    if (params.section) {
      scroller.scrollTo(params.section, { smooth: true, duration: 1000 });
    } else if (params.rowId) {
      scroller.scrollTo(params.rowId, {
        smooth: true,
        duration: 1000,
        offset: -250
      });
    } else if (params.rowIds) {
      const rowIds = tryParseJson(params.rowIds);
      if (rowIds?.length) {
        scroller.scrollTo(rowIds[0], {
          smooth: true,
          duration: 1000,
          offset: -250
        });
      }
    }

    if (params.q && currentDocument) {
      const foundHighlight = mainHighlighter();

      console.log('foundHighlight: ', foundHighlight);

      if (!foundHighlight) {
        console.log('fallback highlight');

        const searchDataGroupedByCategory = {};

        currentDocument.searchData.forEach((item) => {
          searchDataGroupedByCategory[item.groupTitle] = item.data;
        });

        const textToHighlight = getRowsToHighlight(
          searchDataGroupedByCategory,
          params.q
        );

        if (textToHighlight?.length) {
          console.log('textToHighlight: ', textToHighlight);

          const newSearchParams = {
            ...Object.fromEntries(searchParams),
            q: '',
            rowIds: JSON.stringify(textToHighlight)
          };
          if (cdaUrl) newSearchParams.cdaUrl = cdaUrl;
          setSearchParams(newSearchParams);
        }
      }
    }
  }, [currentDocument, searchParams]);

  if (params.pdfUrl) {
    return (
      <PdfFileViewer
        pdfUrl={params.pdfUrl}
        fileName={params.fileName}
        previewPage={params.previewPage}
        searchText={params.searchText}
        metaDataPreview={JSON.parse(params.metaDataPreview)}
        metaDataDebug={JSON.parse(params.metaDataDebug)}
      />
    );
  }

  if (!currentDocument) {
    return <FileUpload ehrRecord={ehrRecord} setFileName={setFileName} />;
  }

  const { header, body } = currentDocument;

  const resetRowsSelection = () => {
    setRowsToExport([]);

    navigate(
      {
        search: ''
      },
      { replace: true }
    );
    setResetRows(true);

    setTimeout(() => {
      setResetRows(false);
    }, 500);
  };

  return (
    <>
      <GlobalStyles />
      <motion.div>
        <Layout
          currentDocument={currentDocument}
          requestPatientRecord={requestPatientRecord}
          isNewRecordAvailable={isNewRecordAvailable}
          rowsToExport={rowsToExport}
          resetRowsSelection={resetRowsSelection}
          rawDocument={rawDocument}
          ehrRecord={ehrRecord}
          fileName={fileName}
          exportCDAAsPDF={exportCDAAsPDF}
          exportCDAAsPDFLoading={exportCDAAsPDFLoading}
          setExportCDAAsPDFLoading={setExportCDAAsPDFLoading}
          documentTitle={params.title}
        >
          <DocumentHeader documentHeaderData={header} />
          {body &&
            body.map((section, i) => (
              <BodySection
                key={i}
                section={section}
                setRowsToExport={setRowsToExport}
                resetRows={resetRows}
                currentDocument={currentDocument}
                rawDocument={rawDocument}
              />
            ))}
        </Layout>
      </motion.div>
    </>
  );
};
